import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Steps, Select, Row, Col, Typography, Divider, Alert, Checkbox } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  BankOutlined,
  UserOutlined,
  ContactsOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from 'Common/Client/Interceptor';
import { toast } from 'react-toastify';
import { Country, State, City } from "country-state-city";

const { Title } = Typography;
const { Option } = Select;

const OrganizationRegistration = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [specialtyAreas, setSpecialtyAreas] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);

  // Country-State-City states
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // NEW: Toggles
  const [sameAsTenantForContact, setSameAsTenantForContact] = useState(false);
  const [sameAsTenantForBilling, setSameAsTenantForBilling] = useState(false);

  // Load all countries
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Initialize form group objects
  useEffect(() => {
    form.setFieldsValue({
      tenantAdmin: {},
      contractAdmin: {},
      billingContact: {},
    });
  }, [form]);

  // Fetch Specialties
  useEffect(() => {
    const fetchSpecialtyAreas = async () => {
      try {
        setLoadingSpecialties(true);
        const response = await api.get('/SpecialityArea/Get-List');
        setSpecialtyAreas(response.data);
      } catch (error) {
        toast.error('Failed to load specialty areas');
      } finally {
        setLoadingSpecialties(false);
      }
    };

    fetchSpecialtyAreas();
  }, []);

  // Handle Country → State
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setSelectedState(null);
    setStatesList(State.getStatesOfCountry(value));
    setCitiesList([]);

    form.setFieldsValue({ state: undefined, city: undefined });
  };

  // Handle State → City
  const handleStateChange = (value) => {
    setSelectedState(value);
    setCitiesList(City.getCitiesOfState(selectedCountry, value));
    form.setFieldsValue({ city: undefined });
  };

  // Auto-fill Contact Admin when toggle ON
  useEffect(() => {
    if (sameAsTenantForContact) {
      const tenant = form.getFieldValue("tenantAdmin") || {};
      form.setFieldsValue({
        contractAdmin: { ...tenant },
      });
    } else {
      form.setFieldsValue({
        contractAdmin: {
          firstName: "",
          middleName: "",
          lastName: "",
          email: "",
          phone: "",
          address: "",
        }
      });
    }
  }, [sameAsTenantForContact]);

  // Auto-fill Billing Admin when toggle ON
  useEffect(() => {
    if (sameAsTenantForBilling) {
      const tenant = form.getFieldValue("tenantAdmin") || {};
      form.setFieldsValue({
        billingContact: { ...tenant },
      });
    } else {
      form.setFieldsValue({
        billingContact: {
          firstName: "",
          middleName: "",
          lastName: "",
          email: "",
          phone: "",
          address: "",
        }
      });
    }
  }, [sameAsTenantForBilling]);

  const steps = [
    { title: 'Organization Details', icon: <BankOutlined /> },
    { title: 'Tenant Administrator', icon: <UserOutlined /> },
    { title: 'Contact Administrator', icon: <ContactsOutlined /> },
    { title: 'Billing Contact', icon: <CreditCardOutlined /> },
  ];

  const allFields = [
    'name', 'specialtyAreaId', 'physicalAdress', 'city', 'state', 'zip', 'country', 'termConditionsAccepted',
    ['tenantAdmin', 'firstName'], ['tenantAdmin', 'lastName'], ['tenantAdmin', 'email'], ['tenantAdmin', 'phone'], ['tenantAdmin', 'address'],
    ['contractAdmin', 'firstName'], ['contractAdmin', 'lastName'], ['contractAdmin', 'email'], ['contractAdmin', 'phone'], ['contractAdmin', 'address'],
    ['billingContact', 'firstName'], ['billingContact', 'lastName'], ['billingContact', 'email'], ['billingContact', 'phone'], ['billingContact', 'address'],
  ];

  const getCurrentStepFields = () => {
    switch (currentStep) {
      case 0:
        return ['name', 'specialtyAreaId', 'physicalAdress', 'city', 'state', 'zip', 'country', 'termConditionsAccepted'];
      case 1:
        return [['tenantAdmin', 'firstName'], ['tenantAdmin', 'lastName'], ['tenantAdmin', 'email'], ['tenantAdmin', 'phone'], ['tenantAdmin', 'address']];
      case 2:
        return sameAsTenantForContact ? [] : [['contractAdmin', 'firstName'], ['contractAdmin', 'lastName'], ['contractAdmin', 'email'], ['contractAdmin', 'phone'], ['contractAdmin', 'address']];
      case 3:
        return sameAsTenantForBilling ? [] : [['billingContact', 'firstName'], ['billingContact', 'lastName'], ['billingContact', 'email'], ['billingContact', 'phone'], ['billingContact', 'address']];
      default:
        return [];
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields(getCurrentStepFields());
      setCurrentStep(currentStep + 1);
    } catch (err) {
      toast.error('Please complete required fields.');
    }
  };

  const handlePrev = () => setCurrentStep(currentStep - 1);
  const handleBack = () => navigate('/startup');

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await form.validateFields(allFields);
      const values = form.getFieldsValue(true);

      const payload = {
        name: values.name,
        physicalAdress: values.physicalAdress,
        zip: values.zip,
        city: values.city,
        state: values.state,
        country: values.country,
        specialtyAreaId: Number(values.specialtyAreaId),
        termConditionsAccepted: values.termConditionsAccepted,

        tenantAdmin: values.tenantAdmin,
        contractAdmin: values.contractAdmin,
        billingContact: values.billingContact,
      };

      await api.post('/Account/Organization/Register', payload);
      navigate('/organization-registration-success', {
        state: { org: values.name, email: values.tenantAdmin?.email },
      });

    } catch (err) {
      const data = err?.response?.data;
      const msg =
        typeof data === 'string'
          ? data
          : data?.message || data?.title || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ minHeight: '100vh', padding: '40px 20px', background: '#FAF8F3' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Card style={{ borderRadius: '12px' }} styles={{ body: { padding: '32px' } }}>
          
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: '16px' }}>
            Back
          </Button>

          <Title level={2} style={{ textAlign: 'center' }}>Organization Registration</Title>

          <Steps current={currentStep} style={{ margin: '32px 0' }} items={steps} />

          <Form form={form} layout="vertical">

            {/* STEP 1 - Organization Details */}
            {currentStep === 0 && (
              <>
                <Form.Item name="name" label="Organization Name" rules={[{ required: true, message: 'Please enter organization name' }]}>
                  <Input placeholder="Organization Name" />
                </Form.Item>

                <Form.Item name="specialtyAreaId" label="Specialty Area" rules={[{ required: true, message: 'Please select specialty area' }]}>
                  <Select placeholder="Select specialty area" loading={loadingSpecialties} showSearch optionFilterProp="children">
                    {specialtyAreas.map(area => <Option key={area.id} value={area.id}>{area.name}</Option>)}
                  </Select>
                </Form.Item>

                <Form.Item name="physicalAdress" label="Physical Address" rules={[{ required: true, message: 'Please enter physical address' }]}>
                  <Input.TextArea rows={3} />
                </Form.Item>

                <Row gutter={16}>
                  {/* Country */}
                  <Col span={6}>
                    <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Please select country' }]}>
                      <Select
                        placeholder="Select country"
                        showSearch
                        optionFilterProp="children"
                        onChange={handleCountryChange}
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={countries.map(c => ({ value: c.isoCode, label: c.name }))}
                      />
                    </Form.Item>
                  </Col>

                  {/* State */}
                  <Col span={6}>
                    <Form.Item name="state" label="State" rules={[{ required: true, message: 'Please select state' }]}>
                      <Select
                        placeholder="Select state"
                        disabled={!selectedCountry}
                        showSearch
                        optionFilterProp="children"
                        onChange={handleStateChange}
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={statesList.map(s => ({ value: s.isoCode, label: s.name }))}
                      />
                    </Form.Item>
                  </Col>

                  {/* City */}
                  <Col span={6}>
                    <Form.Item name="city" label="City" rules={[{ required: true, message: 'Please select city' }]}>
                      <Select
                        placeholder="Select city"
                        disabled={!selectedState}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={citiesList.map(city => ({ value: city.name, label: city.name }))}
                      />
                    </Form.Item>
                  </Col>

                  {/* Zip */}
                  <Col span={6}>
                    <Form.Item name="zip" label="Zip Code" rules={[{ required: true, message: 'Please enter zip code' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="termConditionsAccepted" valuePropName="checked" rules={[{ required: true, message: 'You must accept terms & conditions' }]}>
                  <Checkbox>
                    I agree to the{' '}
                    <a href="https://example.com/terms" target="_blank" rel="noreferrer">Terms & Conditions</a>
                  </Checkbox>
                </Form.Item>
              </>
            )}

            {/* STEP 2 - Tenant Administrator (MAIN ADMIN) */}
            {currentStep === 1 && (
              <>
                <Title level={4}>Tenant Administrator</Title>
                <Alert message="This email will be used to access the organization as admin" type="info" showIcon style={{ marginBottom: 16 }} />
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name={['tenantAdmin','firstName']} label="First Name" rules={[{ required: true, message: 'Please enter first name' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={['tenantAdmin','middleName']} label="Middle Name">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={['tenantAdmin','lastName']} label="Last Name" rules={[{ required: true, message: 'Please enter last name' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name={['tenantAdmin','email']} label="Email" rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Enter a valid email' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['tenantAdmin','phone']} label="Phone" rules={[{ required: true, message: 'Please enter phone' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name={['tenantAdmin','address']} label="Address" rules={[{ required: true, message: 'Please enter address' }]}>
                  <Input.TextArea rows={3} />
                </Form.Item>
              </>
            )}

            {/* STEP 3 - Contact Administrator */}
            {currentStep === 2 && (
              <>
                <Title level={4}>Contact Administrator</Title>

                <Checkbox
                  checked={sameAsTenantForContact}
                  onChange={e => setSameAsTenantForContact(e.target.checked)}
                  style={{ marginBottom: 16 }}
                >
                  Same as Tenant Administrator
                </Checkbox>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name={['contractAdmin','firstName']}
                      label="First Name"
                      rules={!sameAsTenantForContact ? [{ required: true, message: 'Please enter first name' }] : []}
                    >
                      <Input disabled={sameAsTenantForContact} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name={['contractAdmin','middleName']} label="Middle Name">
                      <Input disabled={sameAsTenantForContact} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={['contractAdmin','lastName']}
                      label="Last Name"
                      rules={!sameAsTenantForContact ? [{ required: true, message: 'Please enter last name' }] : []}
                    >
                      <Input disabled={sameAsTenantForContact} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name={['contractAdmin','email']}
                      label="Email"
                      rules={!sameAsTenantForContact ? [{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Enter a valid email' }] : []}
                    >
                      <Input disabled={sameAsTenantForContact} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['contractAdmin','phone']}
                      label="Phone"
                      rules={!sameAsTenantForContact ? [{ required: true, message: 'Please enter phone' }] : []}
                    >
                      <Input disabled={sameAsTenantForContact} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name={['contractAdmin','address']}
                  label="Address"
                  rules={!sameAsTenantForContact ? [{ required: true, message: 'Please enter address' }] : []}
                >
                  <Input.TextArea rows={3} disabled={sameAsTenantForContact} />
                </Form.Item>
              </>
            )}

            {/* STEP 4 - Billing Contact */}
            {currentStep === 3 && (
              <>
                <Title level={4}>Billing Contact</Title>

                <Checkbox
                  checked={sameAsTenantForBilling}
                  onChange={e => setSameAsTenantForBilling(e.target.checked)}
                  style={{ marginBottom: 16 }}
                >
                  Same as Tenant Administrator
                </Checkbox>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name={['billingContact','firstName']}
                      label="First Name"
                      rules={!sameAsTenantForBilling ? [{ required: true, message: 'Please enter first name' }] : []}
                    >
                      <Input disabled={sameAsTenantForBilling} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name={['billingContact','middleName']} label="Middle Name">
                      <Input disabled={sameAsTenantForBilling} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name={['billingContact','lastName']}
                      label="Last Name"
                      rules={!sameAsTenantForBilling ? [{ required: true, message: 'Please enter last name' }] : []}
                    >
                      <Input disabled={sameAsTenantForBilling} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name={['billingContact','email']}
                      label="Email"
                      rules={!sameAsTenantForBilling ? [{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Enter a valid email' }] : []}
                    >
                      <Input disabled={sameAsTenantForBilling} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['billingContact','phone']}
                      label="Phone"
                      rules={!sameAsTenantForBilling ? [{ required: true, message: 'Please enter phone' }] : []}
                    >
                      <Input disabled={sameAsTenantForBilling} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name={['billingContact','address']}
                  label="Address"
                  rules={!sameAsTenantForBilling ? [{ required: true, message: 'Please enter address' }] : []}
                >
                  <Input.TextArea rows={3} disabled={sameAsTenantForBilling} />
                </Form.Item>
              </>
            )}

            <Divider />

            <Row justify="space-between">
              <Col>
                {currentStep > 0 && (
                  <Button onClick={handlePrev} icon={<ArrowLeftOutlined />}>
                    Previous
                  </Button>
                )}
              </Col>

              <Col>
                {currentStep < steps.length - 1 ? (
                  <Button type="primary" onClick={handleNext} icon={<ArrowRightOutlined />}>
                    Next
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleSubmit} loading={loading} icon={<CheckCircleOutlined />}>
                    Register
                  </Button>
                )}
              </Col>
            </Row>

          </Form>
        </Card>
      </div>
    </div>
  );
};

export default OrganizationRegistration;